import axios from 'axios-observable';
import { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { PageableQuery, UUID } from '../app/basicModels';
import { Methodology, MethodologyDefault, MethodologyPage, WorkspaceMethodologiesPage } from './models';
import CONFIG from '../../settings';
import { CommandRequest } from '../../helpers/request/models';
import { MethodologyUpdatePayload } from '../methodologyDeep/models';
import { PhenomenonSingle } from '../models';
import { concatMap, map } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { notification } from '../../components';
import { downloadCSV } from './utils';
import { protectorAppId } from '../cropwisePlans/models';
import { ObservationsConfigV2 } from '../observationsConfigV2';

const METHODOLOGIES_PAGE_SIZE = 300; // cannot be greater than 300
const METHODOLOGIES_BY_COMPANY_PAGE_SIZE = 10;
const demeterMethodologyUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/api/v1/methodologies`;
const baseMethodologyUrl = `${CONFIG.apiUrl}:${CONFIG.basePort}/v1/methodologies`;
const protectorUrl = CONFIG.protectorApiUrl;
const protectorCropwiseUrl = CONFIG.protectorCropwiseApiUrl;
export const cropwiseURL = process.env.REACT_APP_CROPWISE_API_URL || 'http://localhost:8080';

export const getMethodologies = (_query: PageableQuery) => {
  const size = METHODOLOGIES_PAGE_SIZE;
  return getMethodologiesPage({ pageable: { page: 0, size } }).pipe(
    concatMap(({ content, total_pages }) => {
      const pages = Array.from({ length: total_pages - 1 }, (_, i) => i + 1);
      const observables = pages.map(p =>
        getMethodologiesPage({
          pageable: {
            page: p,
            size
          }
        }).pipe(concatMap(methodologiesPage => of(methodologiesPage.content)))
      );
      return forkJoin([of(content), ...observables]);
    }),
    map(methodologies => ({ data: { content: methodologies.flat() } }))
  );
};

export const getMethodologiesPage = (_query: PageableQuery) => {
  return axios
    .get<MethodologyPage>(`${demeterMethodologyUrl}?page=${_query.pageable.page}&size=${_query.pageable.size}&shallow=true`)
    .pipe(map(m => m.data));
};

const getMethodologyByCompanyPage = (companyId: UUID, page = 0, includeWorkspaceMethodologies = true) => {
  const url = `${protectorUrl}/api/v1/methodologies/by-company/${companyId}?page=${page}&size=${METHODOLOGIES_BY_COMPANY_PAGE_SIZE}&show_deleted=false&shallow=true&include_workspace_methodologies=${includeWorkspaceMethodologies}`;

  return axios.get<MethodologyPage>(url, { headers: { 'X-Company-Id': companyId } }).pipe(map(m => m.data));
};

export const getMethodologiesByCompanyPaginated = (companyId: UUID, includeWorkspaceMethodologies = true) => {
  return getMethodologyByCompanyPage(companyId, 0, includeWorkspaceMethodologies).pipe(
    concatMap(({ content, total_pages }) => {
      const pages = Array.from({ length: total_pages - 1 }, (_, i) => i + 1);

      const observables = pages?.map(currentPageNumber =>
        getMethodologyByCompanyPage(companyId, currentPageNumber, includeWorkspaceMethodologies).pipe(
          concatMap(methodologiesPage => of(methodologiesPage?.content))
        )
      );
      return forkJoin([of(content), ...observables]);
    }),
    map(methodologies => methodologies?.flat())
  );
};

/**
 * Lists workspace-level methodologies for the given workspace(s).
 * Backs the "Workspace" branch of the Seeds Setup (P40-61252): unlike by-company,
 * this returns methodologies scoped to the workspace itself (org_id = null-UUID).
 */
export const getMethodologiesByWorkspace = (workspaceIds: UUID[], cursor?: string): Observable<Methodology[]> => {
  const config = cursor ? { params: { cursor } } : undefined;

  return axios
    .post<WorkspaceMethodologiesPage>(
      `${protectorCropwiseUrl}/v1/workspaces/methodologies`,
      {
        workspace_ids: workspaceIds
      },
      config
    )
    .pipe(
      map(response => response.data),
      concatMap(page => {
        const content = page.content ?? [];
        if (page.last || !page.cursor) {
          return of(content);
        }

        return getMethodologiesByWorkspace(workspaceIds, page.cursor).pipe(map(nextPage => [...content, ...nextPage]));
      })
    );
};

export const getMethodology = (methodologyId: UUID): AxiosObservable<Methodology> => {
  return axios.get<Methodology>(`${demeterMethodologyUrl}/${methodologyId}?sort_components=true`);
};

export const saveMethodology = (
  methodology: CommandRequest<Methodology | MethodologyUpdatePayload>,
  isNew: boolean
): AxiosObservable<Methodology> => {
  if (isNew) {
    return createMethodology(methodology as CommandRequest<Methodology>);
  }
  return updateMethodology(methodology as CommandRequest<MethodologyUpdatePayload>);
};

export const cloneMethodology = (methodology: any, methodologyId: UUID, companyId: UUID): AxiosObservable<Methodology> => {
  return ObservationsConfigV2.cloneMethodologyToOrg(methodology.payload, methodologyId, companyId);
};

export const cloneMethodologyToWorkspace = (
  methodology: CommandRequest<Methodology | MethodologyUpdatePayload>,
  methodologyId: UUID,
  workspaceId: UUID
): AxiosObservable<Methodology> => {
  return axios.post<Methodology>(`${demeterMethodologyUrl}/${methodologyId}/clone-to-workspace/${workspaceId}`, methodology);
};

export const createMethodology = (methodology: CommandRequest<Methodology>): AxiosObservable<Methodology> => {
  return ObservationsConfigV2.createMethodology(methodology.payload);
};

export const saveCloneMethodology = (methodology: any, id: UUID, company_id_old: UUID): AxiosObservable<Methodology> => {
  return axios.post<Methodology>(`${demeterMethodologyUrl}/${id}/duplicated`, methodology);
};

export const updateMethodology = (methodology: CommandRequest<MethodologyUpdatePayload>): AxiosObservable<Methodology> => {
  return ObservationsConfigV2.updateMethodology(methodology.payload);
};

export const deleteMethodology = (methodology: CommandRequest<Methodology>): AxiosObservable<Methodology> => {
  const { id } = methodology.payload;
  if (!id) throw new Error('Methodology id is required');
  return ObservationsConfigV2.deleteMethodology(id);
};

export const getMethodologyDefault = (cropId?: UUID, companyId?: UUID): AxiosObservable<MethodologyDefault> => {
  return axios.get<MethodologyDefault>(`${baseMethodologyUrl}?companyId=${companyId}&cropId=${cropId}`);
};

export const getPhenomenaByMethodology = (methodologyId: UUID): AxiosObservable<PhenomenonSingle[]> => {
  return axios.get<PhenomenonSingle[]>(`${demeterMethodologyUrl}/${methodologyId}/phenomenon`);
};

export const getExtraDimensionsByMethodology = (methodologyId: UUID): AxiosObservable<any> => {
  return axios.get<any>(`${demeterMethodologyUrl}/${methodologyId}/extra-dimensions`);
};

const downloadMethodologyById = (methodologyId: UUID): AxiosObservable<any> => {
  const configs = { headers: { 'content-type': 'text/csv' }, data: {} };
  return axios.get<any>(`${CONFIG.apiUrl}/api/v1/methodologies/${methodologyId}/with-references`, configs);
};

export const fetchDownloadMethodologyById = methodologyId => {
  downloadMethodologyById(methodologyId)
    .toPromise()
    .then(response => {
      downloadCSV(response.data);
    })
    .catch(err => {
      console.error(err);
      notification('error', 'Não foi possivel fazer o download. Verifique a metodologia');
    });
};

export const getOrgsThatUserHasAccessPage = (_query: PageableQuery) => {
  const params = {
    page: _query.pageable.page,
    size: _query.pageable.size,
    app_id: protectorAppId
  };

  return axios
    .get(`${cropwiseURL}/v2/orgs`, {
      params
    })
    .pipe(map(response => response.data));
};

export const getOrgsThatUserHasAccess = (_query: PageableQuery) => {
  const size = 5000;
  return getOrgsThatUserHasAccessPage({ pageable: { page: 0, size } }).pipe(
    concatMap(({ content, total_pages }) => {
      const pages = Array.from({ length: total_pages - 1 }, (_, i) => i + 1);

      const observables = pages.map(p =>
        getOrgsThatUserHasAccessPage({
          pageable: {
            page: p,
            size
          }
        }).pipe(concatMap(orgsPage => of(orgsPage.content)))
      );

      return forkJoin([of(content), ...observables]);
    }),
    map(methodologies => ({ data: { content: methodologies.flat() } }))
  );
};
